import { useEffect, useState } from "react";
import { isAndroid, isIos, isIosSafari, isStandalone } from "../../pwa/platform";
import { canPromptInstall, onInstallChange, promptInstall, wasInstalled } from "../../pwa/install";
import { tapFeedback } from "../feedback";

/**
 * How to put Pointz on the home screen, for this phone. iOS has no install API, so it gets the
 * Share sheet steps; Chromium on Android gets the browser's own prompt when it has offered one,
 * else the Chrome menu steps. A desktop or unknown browser sees both sets, for the phone.
 */
export function InstallGuide({ onBack }: { onBack: () => void }) {
  const [canPrompt, setCanPrompt] = useState(canPromptInstall);
  const [installed, setInstalled] = useState(() => isStandalone() || wasInstalled());
  const [installing, setInstalling] = useState(false);

  useEffect(
    () =>
      onInstallChange(() => {
        setCanPrompt(canPromptInstall());
        setInstalled(isStandalone() || wasInstalled());
      }),
    [],
  );

  const ios = isIos();
  const android = isAndroid();
  const showIos = ios || !android;
  const showAndroid = android || !ios;

  async function install() {
    tapFeedback();
    setInstalling(true);
    try {
      await promptInstall();
    } finally {
      setInstalling(false);
    }
  }

  return (
    <main className="screen">
      <button className="back" type="button" onClick={onBack}>
        <span aria-hidden="true">‹</span> Back
      </button>
      <h1 className="title">Install Pointz</h1>

      {installed ? (
        <p className="lead">Pointz is installed. Open it from your home screen any time.</p>
      ) : (
        <p className="lead">On the home screen Pointz opens full screen, one tap away, like any other app.</p>
      )}

      {!installed && showIos && (
        <section className="section install">
          <h2 className="section__title">iPhone and iPad</h2>
          {ios && !isIosSafari() ? (
            <p className="hint">
              Adding to the home screen only works in <strong>Safari</strong>. Open this page in Safari, then follow
              these steps.
            </p>
          ) : null}
          <ol className="install__steps">
            <li>
              Tap <strong>Share</strong> (the square with an arrow) in Safari's toolbar.
            </li>
            <li>
              Scroll down and tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.
            </li>
            <li>
              Open <strong>Pointz</strong> from your home screen and sign in there.
            </li>
          </ol>
          {/* Without this, parents who signed in in Safari and then installed were surprised to be
              asked to sign in again inside the app. */}
          <p className="install__note">
            The home-screen app keeps its own sign-in, separate from Safari, so you sign in once more inside it:
            grown-ups with an email code, kids with their name and code. After that it stays signed in.
          </p>
        </section>
      )}

      {!installed && showAndroid && (
        <section className="section install">
          <h2 className="section__title">Android</h2>
          {canPrompt ? (
            <button
              className="button button--primary"
              type="button"
              disabled={installing}
              onClick={() => void install()}
            >
              {installing ? "Installing…" : "Install Pointz"}
            </button>
          ) : (
            <ol className="install__steps">
              <li>
                Tap the <strong>⋮</strong> menu in Chrome.
              </li>
              <li>
                Choose <strong>Install app</strong> (or <strong>Add to Home screen</strong>).
              </li>
              <li>
                Open <strong>Pointz</strong> from your home screen.
              </li>
            </ol>
          )}
          <p className="install__note">On Android the app shares Chrome's sign-in, so you stay signed in.</p>
        </section>
      )}
    </main>
  );
}
